import React, { useState } from 'react';
import { Container, Box, TextField, Button, Typography, Alert, Divider, Link } from '@mui/material';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import GoogleIcon from '@mui/icons-material/Google';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://watchpassion.vercel.app/api';

export default function LoginPage() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';

  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      navigate(user.role === 'admin' ? '/admin' : from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Errore di accesso');
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setGoogleLoading(true);
      setError('');
      try {
        // Exchange access token for user info, then send to backend
        const { data: userInfo } = await axios.get('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        const user = await loginWithGoogle(userInfo.email, userInfo.name, userInfo.sub);
        navigate(user.role === 'admin' ? '/admin' : from, { replace: true });
      } catch (err) {
        setError('Errore durante il login con Google');
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: () => setError('Login Google annullato o non riuscito'),
  });

  return (
    <Container maxWidth="xs" sx={{ pt: 8, pb: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>Accedi</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Non hai un account?{' '}
        <Link component={RouterLink} to="/registrati">Registrati</Link>
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box component="form" onSubmit={handleSubmit}>
        <TextField fullWidth label="Email" name="email" type="email" value={form.email}
          onChange={handleChange} sx={{ mb: 2 }} required autoComplete="email" />
        <TextField fullWidth label="Password" name="password" type="password" value={form.password}
          onChange={handleChange} sx={{ mb: 3 }} required autoComplete="current-password" />
        <Button fullWidth type="submit" variant="contained" size="large" disabled={loading}>
          {loading ? 'Accesso...' : 'Accedi'}
        </Button>
      </Box>

      <Divider sx={{ my: 3 }}>oppure</Divider>

      <Button
        fullWidth variant="outlined" size="large"
        startIcon={<GoogleIcon />}
        onClick={() => googleLogin()}
        disabled={googleLoading}
        sx={{ mb: 1 }}
      >
        {googleLoading ? 'Accesso con Google...' : 'Accedi con Google'}
      </Button>
    </Container>
  );
}
